import { ACCOUNT_ACTION_IDS } from '@central-products/interface-account-actions';
import * as MyAccountModals from '@myaccount/modals';
import { subscribe, publish, publishSticky, unsubscribe } from '@wh/message-bus';
import { StateType } from '@central-products/interface-session-service';
import { feature as MyAccountFeature } from '@myaccount/myaccount';
import * as MyAccountHeaderFeature from '@myaccount/pre-integrated-feature';
import * as SaferGamblingModals from '@myaccount/safer-gambling-modals';
import { EN_GB, IT_IT, JA_JP, RU_RU, DE_DE, ES_ES } from '@myaccount/translations';
import { getValueForDevice, getValueFromContent, openLink } from '../../utils';

const getTranslation = (locale) => {
  if (typeof locale !== 'string') {
    throw new Error(`Unsupported locale type. Expected string, got ${typeof locale}`);
  }
  switch (locale.toLowerCase()) {
    case 'en-gb':
      return EN_GB;
    case 'it-it':
      return IT_IT;
    case 'ja-jp':
      return JA_JP;
    case 'ru-ru':
      return RU_RU;
    case 'de-de':
      return DE_DE;
    case 'de-at':
      return DE_DE;
    case 'es-es':
      return ES_ES;
    default:
      return EN_GB;
  }
};

const openLogin = () => {
  publish('wh.common.event.ui.header.login.requested');
};

const openMyAccountPage = (viewUrlParam) => {
  if (viewUrlParam === 'personalDetailsMarketing') {
    publishSticky('wh.myAccount.command.dropdown.open', { path: '/PersonalDetails/Marketing' });
    return;
  }
  publishSticky('wh.myAccount.command.dropdown.open', {
    path: viewUrlParam,
  });
};

const openAccountPageIfNeeded = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const rawUrl = window.location.toString().split('?')[0];
  const urlparams = new URLSearchParams(window.location.search);
  const viewUrlParam = urlparams.get('myAccountView');
  if (!viewUrlParam) {
    return;
  }
  const sessionStateGet = subscribe({
    topic: 'wh.common.event.session.state.get',
    callback: ({ data }) => {
      if (data.state === 'NOT_LOGGED_IN') {
        openLogin();
      }
    },
  });

  const stateChangedSubscribeId = subscribe({
    topic: 'wh.common.event.sessionService.state.changed',
    callback: async ({ data }) => {
      if (data.state !== StateType.SESSION_EXISTS) {
        return;
      }
      unsubscribe(sessionStateGet);
      unsubscribe(stateChangedSubscribeId);

      openMyAccountPage(viewUrlParam);
      urlparams.delete('myAccountView');
      const queryStr = urlparams.toString();
      window.history.pushState(null, '', `${rawUrl}${queryStr ? '?' + queryStr : ''}`);
    },
  });
};

export const myAccountPlugin = {
  beforeBootstrap({ addBootstrapperFeature, locale }) {
    addBootstrapperFeature(MyAccountFeature, { translations: getTranslation(locale) });
    addBootstrapperFeature(SaferGamblingModals.feature, {
      translations: getTranslation(locale),
    });
  },
  beforeRender({ header, content, params, headerRenderers, locale }) {
    content.myAccountTranslations = getTranslation(locale);

    const myAccountButtonConfig = {
      type: '@myaccount/myaccount-button',
      data: {
        id: 'hrs-action__myaccount-button',
      },
      children: [],
    };

    MyAccountHeaderFeature.initWorkflow();
    MyAccountModals.initWorkflow();
    SaferGamblingModals.workflow();

    const myAccountModalsConfig = {
      type: '@myaccount/modals',
      data: {
        id: 'pi-header__myaccount-modals',
        urls: {
          withdraw: getValueForDevice(getValueFromContent(params.withdrawUrl, content)),
          reversalWithdraw: getValueForDevice(getValueFromContent(params.reversalWithdrawUrl, content)),
          gamblingControls: getValueForDevice(getValueFromContent(params.gamblingControlsUrl, content)),
          pokerBalanceTransfer: getValueForDevice(getValueFromContent(params.pokerBalanceTransferUrl, content)),
        },
      },
      children: [],
    };

    header.actions.children.splice(1, 0, myAccountButtonConfig);
    header.actions.children.push(myAccountModalsConfig);
    headerRenderers.push(MyAccountModals.modals);
    MyAccountHeaderFeature.renderers.forEach((renderer) => {
      headerRenderers.push(renderer);
    });
    openAccountPageIfNeeded();
  },
  postBootstrap({ bootstrapperFeatures }) {
    bootstrapperFeatures['account-actions'].onActionRequested(({ actionId }) => {
      if (actionId === ACCOUNT_ACTION_IDS.MYACCOUNT_START) {
        bootstrapperFeatures['my-account'].start();
      } else if (actionId === ACCOUNT_ACTION_IDS.MYACCOUNT_CANCEL) {
        bootstrapperFeatures['my-account'].cancel();
      } else if (actionId === ACCOUNT_ACTION_IDS.SAFER_GAMBLING_MODALS_START) {
        bootstrapperFeatures['safer-gambling-modals'].start();
      }
    });
  },
  afterRender({ params, content }) {
    const MY_ACCOUNT_OPEN_REWARDS_REQUESTED = 'wh.myAccount.event.navigationMenu.select.rewards';
    const MY_ACCOUNT_OPEN_FEEDBACK_REQUESTED = 'wh.myAccount.event.footer.request.feedback';
    const MY_ACCOUNT_OPEN_HELP_REQUESTED = 'wh.myAccount.event.footer.request.help';

    const promotionsUrl = getValueFromContent(params.promotionsPageUrl, content);
    const helpPageUrl = getValueFromContent(params.helpPageUrl, content);
    const openFeedbackTopic = getValueFromContent(params.openFeedbackTopic, content);

    if (promotionsUrl) {
      subscribe(MY_ACCOUNT_OPEN_REWARDS_REQUESTED, () => openLink({ url: promotionsUrl }));
    }

    if (helpPageUrl) {
      subscribe(MY_ACCOUNT_OPEN_HELP_REQUESTED, () => openLink({ url: helpPageUrl, target: '_blank' }));
    }

    if (openFeedbackTopic) {
      subscribe(MY_ACCOUNT_OPEN_FEEDBACK_REQUESTED, () => publish({ topic: openFeedbackTopic }));
    }
    subscribe({
      topic: 'wh.kraken.command.myaccount.balance.hide',
      callback: () => {
        publish('wh.myAccount.command.balance.hide');
      },
    });
    subscribe({
      topic: 'wh.kraken.command.myaccount.balance.show',
      callback: () => {
        publish('wh.myAccount.command.balance.show');
      },
    });
    subscribe({
      topic: 'wh.common.event.ui.header.deposit.closed',
      callback: () => {
        // trigger myAccount refresh balance by use balance-and-bonus-essentials event listener
        publish('refreshBalance');
      },
    });
  },
};
